/**
 * Known bot user-agent patterns.
 * Based on travel_service `bot-detection.util.ts`, plus Google's non-Googlebot crawlers and headless Chrome.
 */
const BOT_USER_AGENT_PATTERNS: ReadonlyArray<RegExp> = [
  /Googlebot/i,
  /Google-InspectionTool/i,
  /AdsBot-Google/i,
  /Mediapartners-Google/i,
  /HeadlessChrome/i,
  /Bingbot/i,
  /Baiduspider/i,
  /YandexBot/i,
  /Applebot/i,
  /Bytespider/i,
  /TikTokSpider/i,
  /Facebot/i,
  /facebookexternalhit/i,
  /Twitterbot/i,
  /Stripebot/i,
  /wpbot/i,
];

/**
 * User-agent patterns that should bypass bot detection.
 * Used by internal E2E test runners (Browser Use) that set `navigator.webdriver`
 * but need travel API calls to fire normally.
 *
 * This is client-side bot/crawler optimization only; any client can spoof
 * the UA, so do not use this allowlist as a security boundary. If server-side
 * gating/rate limiting/content entitlement ever depends on this signal,
 * replace it with a signed header or shared secret.
 */
const E2E_ALLOWLIST_PATTERNS: ReadonlyArray<RegExp> = [/JoyE2ETest/i];

let cachedResult: boolean | undefined;

/**
 * Returns `true` when the current client is a known bot/crawler.
 *
 * Checks `navigator.userAgent` for known bot patterns and
 * `navigator.webdriver` (set by Googlebot's headless Chrome renderer).
 *
 * E2E test runners that include "JoyE2ETest" in their user-agent are
 * excluded so travel API calls fire normally during automated tests.
 *
 * Result is memoized — the user-agent doesn't change during page lifecycle.
 * Returns `false` during SSR (no `navigator`).
 */
export function isBotClient(): boolean {
  if (cachedResult !== undefined) {
    return cachedResult;
  }

  if (typeof navigator === 'undefined') {
    return false;
  }

  const ua = navigator.userAgent;

  // Allow internal E2E test runners through
  if (ua && E2E_ALLOWLIST_PATTERNS.some(pattern => pattern.test(ua))) {
    cachedResult = false;
    return false;
  }

  const isBot = (ua && BOT_USER_AGENT_PATTERNS.some(pattern => pattern.test(ua))) || navigator.webdriver === true;

  cachedResult = isBot;
  return isBot;
}
